import React, { useState, useEffect } from 'react';
import { Download } from 'lucide-react';
import { API_URL, fetchWithAuth } from '../api';
import { getPHTDate } from '../utils';
import { formatCurrency } from '../utils/formatUtils';
import { styles } from '../theme';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

export const DailySalesPerShiftReport = ({ token }) => {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [branchId, setBranchId] = useState('');
  const [branches, setBranches] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Default data to past 7 days
    const to = getPHTDate();
    const from = getPHTDate();
    from.setDate(from.getDate() - 7);
    
    const formatDateStr = (d) => {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };

    const fromStr = formatDateStr(from);
    const toStr = formatDateStr(to);

    setDateFrom(fromStr);
    setDateTo(toStr);
    
    fetchBranches();
    autoGenerateReport(fromStr, toStr, '');
  }, []);

  const fetchBranches = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/branchesdropdown`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const userRole = userData.role?.toLowerCase();
      const allowedIds = (userData.allowed_branches || []).map(b => Number(b.id));

      const authorizedBranches = data.filter(branch => {
        if (userRole === 'administrator') return true;
        return allowedIds.includes(Number(branch.id));
      });
      setBranches(authorizedBranches);
    } catch (err) {
      console.error('Error fetching branches:', err);
    }
  };

  const autoGenerateReport = async (from, to, branch) => {
    setLoading(true);
    try {
      let url = `${API_URL}/reports/daily-sales-per-shift?dateFrom=${from}&dateTo=${to}`;
      if (branch) url += `&branchId=${branch}`;

      const res = await fetchWithAuth(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setReportData(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error generating report:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateReport = () => {
    autoGenerateReport(dateFrom, dateTo, branchId);
  };

  const calculateTotals = () => {
    return reportData.reduce(
      (acc, item) => {
        const s1 = Number(item['Shift 1'] || 0);
        const s2 = Number(item['Shift 2'] || 0);
        acc.shift1 += s1;
        acc.shift2 += s2;
        acc.total += s1 + s2;
        return acc;
      },
      { shift1: 0, shift2: 0, total: 0 }
    );
  };

  const totals = calculateTotals();

  const formatDateLabel = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const d = new Date(parts[0], parts[1] - 1, parts[2]);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
    return dateStr;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const totalDailySales = payload.reduce((sum, entry) => sum + Number(entry.value || 0), 0);
      return (
        <div style={{ backgroundColor: '#ffffff', padding: '12px 16px', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)', border: '1px solid #e5e7eb' }}>
          <p style={{ fontWeight: '600', color: '#1f2937', marginBottom: '8px', marginTop: 0 }}>{formatDateLabel(label)}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color, margin: '4px 0', fontSize: '13px', display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
              <span>{entry.name}:</span>
              <span style={{ fontWeight: '600' }}>₱{formatCurrency(entry.value)}</span>
            </p>
          ))}
          <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '8px', paddingTop: '8px', display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
            <span style={{ fontWeight: '700', color: '#111827', fontSize: '13px' }}>Total Daily Sales:</span>
            <span style={{ fontWeight: '700', color: '#4f46e5', fontSize: '13px' }}>₱{formatCurrency(totalDailySales)}</span>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', marginBottom: '24px' }}>
        Daily Sales Report per shift
      </h1>

      <div style={styles.card}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
          <div style={styles.formGroup}>
            <label style={styles.label}>From Date</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              style={styles.input}
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>To Date</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              style={styles.input}
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Branch</label>
            <select
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              style={styles.input}
            >
              <option value="">All Branches</option>
              {branches.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={generateReport}
            disabled={loading}
            style={{ ...styles.button, ...styles.buttonReport }}
          >
            <Download style={{ width: '20px', height: '20px' }} />
            {loading ? 'Generating...' : 'Generate Report'}
          </button>
        </div>
      </div>

      {reportData && reportData.length > 0 && (
        <div style={{ marginTop: '24px' }}>
          <div style={{ ...styles.card, marginBottom: '24px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '20px' }}>
              Shift Sales Comparison (Stacked Bar Graph)
            </h3>
            <div style={{ height: '360px', width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={reportData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={formatDateLabel} 
                    tick={{ fill: '#4b5563', fontSize: 12 }}
                  />
                  <YAxis 
                    tickFormatter={(val) => `₱${val.toLocaleString()}`}
                    tick={{ fill: '#4b5563', fontSize: 12 }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ paddingTop: '10px' }} />
                  <Bar dataKey="Shift 1" stackId="a" fill="#4f46e5" name="Shift 1" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="Shift 2" stackId="a" fill="#06b6d4" name="Shift 2" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div style={styles.card}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '16px' }}>
              Daily Sales Breakdown
            </h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Date</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Shift 1 Net Sales</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Shift 2 Net Sales</th>
                    <th style={{ ...styles.th, textAlign: 'right' }}>Total Daily Sales</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.map((row, index) => {
                    const shift1 = Number(row['Shift 1'] || 0);
                    const shift2 = Number(row['Shift 2'] || 0);
                    const rowTotal = shift1 + shift2;

                    return (
                      <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb' }}>
                        <td style={styles.td}>{formatDateLabel(row.date)}</td>
                        <td style={{ ...styles.td, textAlign: 'right' }}>₱{formatCurrency(shift1)}</td>
                        <td style={{ ...styles.td, textAlign: 'right' }}>₱{formatCurrency(shift2)}</td>
                        <td style={{ ...styles.td, textAlign: 'right', fontWeight: '600', color: '#111827' }}>
                          ₱{formatCurrency(rowTotal)}
                        </td>
                      </tr>
                    );
                  })}
                  <tr style={{ backgroundColor: '#ecfeff' }}>
                    <td style={{ ...styles.td, fontWeight: '800', color: '#0e7490' }}>PERIOD TOTAL</td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: '800', color: '#0e7490' }}>
                      ₱{formatCurrency(totals.shift1)}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: '800', color: '#0e7490' }}>
                      ₱{formatCurrency(totals.shift2)}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: '800', color: '#0e7490' }}>
                      ₱{formatCurrency(totals.total)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {reportData && reportData.length === 0 && !loading && (
        <div style={{ ...styles.card, marginTop: '24px', textAlign: 'center', padding: '48px', color: '#6b7280' }}>
          No sales data found for the selected date range.
        </div>
      )}
    </div>
  );
};
